import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import useExport from 'hooks/useExport';

import FieldChecklist from './FieldChecklist';

const ExportModal = ({
  isOpen,
  onClose,
  entity,
  title,
  currentFilters = {},
  filteredTotal,
  matchTableKeys = [],
}) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor = useColorModeValue('secondaryGray.600', 'white');

  const {
    fields,
    isLoadingFields,
    loadFields,
    format,
    setFormat,
    scope,
    setScope,
    selectedFields,
    toggleField,
    applyPreset,
    runExport,
    isExporting,
    rowCountLabel,
  } = useExport({ entity, currentFilters, filteredTotal });

  useEffect(() => {
    if (isOpen) loadFields();
  }, [isOpen, loadFields]);

  const sectionLabelProps = {
    fontSize: 'xs',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: mutedColor,
    mb: 2,
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      placement="right"
      size={{ base: 'full', md: 'lg' }}
    >
      <DrawerOverlay backdropFilter="blur(2px)" />
      <DrawerContent>
        <DrawerCloseButton top={4} right={4} color={textColor} />
        <DrawerHeader fontSize="lg" fontWeight="700" color={textColor} pb={1}>
          {title}
        </DrawerHeader>

        <DrawerBody pb={2}>
          <Text {...sectionLabelProps}>Format</Text>
          <RadioGroup value={format} onChange={setFormat} mb={4}>
            <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
              <Radio value="csv" colorScheme="brand">
                <Text color={textColor} fontSize="sm">
                  CSV
                </Text>
              </Radio>
              <Radio value="xlsx" colorScheme="brand">
                <Text color={textColor} fontSize="sm">
                  Excel (.xlsx)
                </Text>
              </Radio>
            </Stack>
          </RadioGroup>

          <Text {...sectionLabelProps}>Rows to export</Text>
          <RadioGroup value={scope} onChange={setScope} mb={4}>
            <Stack direction="column" spacing={2}>
              <Radio
                value="filtered"
                colorScheme="brand"
                alignItems="flex-start"
              >
                <Box>
                  <Text fontWeight="600" fontSize="sm" color={textColor}>
                    Current filter results
                  </Text>
                  <Text fontSize="xs" fontWeight="400" color={textColor}>
                    Respects the active tab, search and status filters.
                  </Text>
                </Box>
              </Radio>
              <Radio value="all" colorScheme="brand" alignItems="flex-start">
                <Box>
                  <Text fontWeight="600" fontSize="sm" color={textColor}>
                    All records
                  </Text>
                  <Text fontSize="xs" fontWeight="400" color={textColor}>
                    Full dataset, ignoring active filters.
                  </Text>
                </Box>
              </Radio>
            </Stack>
          </RadioGroup>

          <Text {...sectionLabelProps}>Fields</Text>
          <FieldChecklist
            fields={fields}
            isLoading={isLoadingFields}
            selectedFields={selectedFields}
            onToggleField={toggleField}
            onApplyPreset={applyPreset}
            matchTableKeys={matchTableKeys}
          />
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px">
          <Flex
            width="100%"
            align="center"
            justify="space-between"
            direction={{ base: 'column', md: 'row' }}
            gap={3}
          >
            <Text fontSize="sm" fontWeight="700" color={textColor}>
              {rowCountLabel} rows &middot; {selectedFields.size} field
              {selectedFields.size === 1 ? '' : 's'}
            </Text>
            <Button
              bg="brand.500"
              color="white"
              _hover={{ bg: 'brand.600' }}
              borderRadius="10px"
              width={{ base: '100%', md: 'auto' }}
              isLoading={isExporting}
              loadingText="Exporting..."
              onClick={runExport}
            >
              Export
            </Button>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

ExportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  entity: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  currentFilters: PropTypes.object,
  filteredTotal: PropTypes.number,
  matchTableKeys: PropTypes.arrayOf(PropTypes.string),
};

export default ExportModal;
